import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { BookOpen, FileDown, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import { useAuth } from '../contexts/AuthContext';
import { useChurch } from '../contexts/ChurchContext';
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';
import './RehearsalReadingsPage.css';

type ReadingKind = 'scripture' | 'christian_lineage';
type ReadingStatus = 'draft' | 'ready';
type SentenceSeason =
  | 'any_time' | 'advent' | 'christmas' | 'epiphany' | 'lent'
  | 'holy_week' | 'easter' | 'trinity' | 'saints' | 'thanksgiving';
type SentenceSource = 'bcp' | 'parish';
type PrayerForm = 'suffrages' | 'collect' | 'prayer' | 'thanksgiving';
type PrayerRite = 'one' | 'two' | 'other';

interface RehearsalReading {
  id: number;
  church_id: number;
  kind: ReadingKind;
  citation: string;
  author?: string | null;
  work?: string | null;
  locator?: string | null;
  text_body: string;
  translation_note?: string | null;
  tags: string[];
  status: ReadingStatus;
  usage_dates?: string[];
  updated_at: string;
}

interface OpeningSentence {
  id: number;
  church_id: number;
  text_body: string;
  citation: string;
  season: SentenceSeason;
  source: SentenceSource;
  bcp_page?: string | null;
  drawn_from_reading_id?: number | null;
  drawn_from_citation?: string | null;
  notes?: string | null;
  usage_dates?: string[];
}

interface RehearsalPrayer {
  id: number;
  church_id: number;
  title: string;
  form: PrayerForm;
  rite: PrayerRite;
  body: string;
  source?: string | null;
  notes?: string | null;
  period_labels?: string[];
}

interface PrayerPeriod {
  id: number;
  church_id: number;
  prayer_id: number;
  label: string;
  starts_on: string;
  ends_on?: string | null;
  prayer_title?: string;
  prayer_form?: PrayerForm;
  prayer_rite?: PrayerRite;
  prayer_body?: string;
  prayer_source?: string | null;
}

interface PsalmSelection {
  citation: string;
  bcpPages: string;
}

interface RehearsalReadingSet {
  id: number;
  church_id: number;
  church_name: string;
  rehearsal_date: string;
  theme: string;
  canticle_name: string;
  canticle_reference: string;
  canticle_bcp_page: string;
  canticle_position: 'before_psalms' | 'after_scripture';
  psalmody: PsalmSelection[];
  opening_sentence_id?: number | null;
  opening_sentence: OpeningSentence | null;
  prayer: (RehearsalPrayer & {
    period_label: string;
    period_starts_on: string;
    period_ends_on: string | null;
  }) | null;
  scripture: RehearsalReading;
  christian_lineage: RehearsalReading;
  status: ReadingStatus;
}

interface ReadingFormState {
  kind: ReadingKind;
  citation: string;
  author: string;
  work: string;
  locator: string;
  text_body: string;
  translation_note: string;
  tags: string;
  status: ReadingStatus;
}

interface SentenceFormState {
  text_body: string;
  citation: string;
  season: SentenceSeason;
  source: SentenceSource;
  bcp_page: string;
  drawn_from_reading_id: string;
  notes: string;
}

interface PrayerFormState {
  title: string;
  form: PrayerForm;
  rite: PrayerRite;
  body: string;
  source: string;
  notes: string;
}

interface PeriodFormState {
  label: string;
  prayer_id: string;
  starts_on: string;
  ends_on: string;
}

interface PreviewContent {
  eyebrow: string;
  title: string;
  note?: string | null;
  text: string;
  onEdit?: () => void;
}

const blankForm: ReadingFormState = {
  kind: 'scripture',
  citation: '',
  author: '',
  work: '',
  locator: '',
  text_body: '',
  translation_note: '',
  tags: '',
  status: 'draft',
};

const blankSentence: SentenceFormState = {
  text_body: '',
  citation: '',
  season: 'any_time',
  source: 'parish',
  bcp_page: '',
  drawn_from_reading_id: '',
  notes: '',
};

const blankPrayer: PrayerFormState = {
  title: '',
  form: 'collect',
  rite: 'two',
  body: '',
  source: '',
  notes: '',
};

const blankPeriod: PeriodFormState = {
  label: '',
  prayer_id: '',
  starts_on: '',
  ends_on: '',
};

const SEASON_LABELS: Record<SentenceSeason, string> = {
  any_time: 'At any time',
  advent: 'Advent',
  christmas: 'Christmas',
  epiphany: 'Epiphany',
  lent: 'Lent',
  holy_week: 'Holy Week',
  easter: 'Easter',
  trinity: 'Trinity Sunday',
  saints: 'Saints',
  thanksgiving: 'Thanksgiving',
};

const SEASON_ORDER: SentenceSeason[] = [
  'any_time', 'advent', 'christmas', 'epiphany', 'lent',
  'holy_week', 'easter', 'trinity', 'saints', 'thanksgiving',
];

const PRAYER_FORM_LABELS: Record<PrayerForm, string> = {
  suffrages: 'Suffrages',
  collect: 'Collect',
  prayer: 'Prayer',
  thanksgiving: 'Thanksgiving',
};

const RITE_LABELS: Record<PrayerRite, string> = {
  one: 'Rite One',
  two: 'Rite Two',
  other: 'Other',
};

function formatDate(value: string, compact = false): string {
  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', compact
    ? { month: 'short', day: 'numeric' }
    : { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function readingKindLabel(kind: ReadingKind): string {
  return kind === 'scripture' ? 'Scripture' : 'Christian lineage';
}

function sentenceSourceLabel(sentence: OpeningSentence): string {
  if (sentence.source === 'bcp') {
    return sentence.bcp_page ? `BCP p. ${sentence.bcp_page}` : 'BCP';
  }
  return 'Ours';
}

function truncate(value: string, limit = 96): string {
  const collapsed = value.replace(/\s+/g, ' ').trim();
  return collapsed.length > limit ? `${collapsed.slice(0, limit - 1)}…` : collapsed;
}

const StatusBadge: React.FC<{ status: ReadingStatus }> = ({ status }) => (
  <Badge bg={status === 'ready' ? 'success' : 'secondary'}>
    {status === 'ready' ? 'Ready' : 'Draft'}
  </Badge>
);

const RehearsalReadingsPage: React.FC = () => {
  const { currentChurch } = useChurch();
  const { user } = useAuth();
  const canEdit = user?.role_name?.toLowerCase() === 'admin';
  const [view, setView] = useState<'schedule' | 'library' | 'sentences' | 'prayers'>('schedule');
  const [sets, setSets] = useState<RehearsalReadingSet[]>([]);
  const [library, setLibrary] = useState<RehearsalReading[]>([]);
  const [sentences, setSentences] = useState<OpeningSentence[]>([]);
  const [prayers, setPrayers] = useState<RehearsalPrayer[]>([]);
  const [periods, setPeriods] = useState<PrayerPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [assigningSetId, setAssigningSetId] = useState<number | null>(null);
  const [scheduleSort, setScheduleSort] = useState<{ key: 'date' | 'theme' | 'status'; asc: boolean }>({ key: 'date', asc: true });
  const [librarySort, setLibrarySort] = useState<{ key: 'kind' | 'citation' | 'status' | 'updated'; asc: boolean }>({ key: 'kind', asc: true });
  const [preview, setPreview] = useState<PreviewContent | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [readingForm, setReadingForm] = useState<ReadingFormState>(blankForm);
  const [sentenceEditorOpen, setSentenceEditorOpen] = useState(false);
  const [editingSentenceId, setEditingSentenceId] = useState<number | null>(null);
  const [sentenceForm, setSentenceForm] = useState<SentenceFormState>(blankSentence);
  const [prayerEditorOpen, setPrayerEditorOpen] = useState(false);
  const [editingPrayerId, setEditingPrayerId] = useState<number | null>(null);
  const [prayerForm, setPrayerForm] = useState<PrayerFormState>(blankPrayer);
  const [periodEditorOpen, setPeriodEditorOpen] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null);
  const [periodForm, setPeriodForm] = useState<PeriodFormState>(blankPeriod);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!currentChurch?.id) return;
    try {
      setLoading(true);
      setError(null);
      const query = `?churchId=${currentChurch.id}`;
      const [scheduleResponse, libraryResponse, sentenceResponse, prayerResponse, periodResponse] = await Promise.all([
        apiClient.get(`/rehearsal-readings/schedules${query}`),
        apiClient.get(`/rehearsal-readings/library${query}`),
        apiClient.get(`/rehearsal-readings/opening-sentences${query}`),
        apiClient.get(`/rehearsal-readings/prayers${query}`),
        apiClient.get(`/rehearsal-readings/prayer-periods${query}`),
      ]);
      setSets(scheduleResponse.data.data || []);
      setLibrary(libraryResponse.data.data || []);
      setSentences(sentenceResponse.data.data || []);
      setPrayers(prayerResponse.data.data || []);
      setPeriods(periodResponse.data.data || []);
    } catch (requestError) {
      console.error('Failed to load rehearsal readings:', requestError);
      setError('The rehearsal readings could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [currentChurch?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedSets = useMemo(() => {
    const copy = [...sets];
    copy.sort((a, b) => {
      const left = scheduleSort.key === 'date' ? a.rehearsal_date : a[scheduleSort.key];
      const right = scheduleSort.key === 'date' ? b.rehearsal_date : b[scheduleSort.key];
      return left.localeCompare(right) * (scheduleSort.asc ? 1 : -1);
    });
    return copy;
  }, [sets, scheduleSort]);

  const sortedLibrary = useMemo(() => {
    const copy = [...library];
    copy.sort((a, b) => {
      const left = librarySort.key === 'updated' ? a.updated_at : a[librarySort.key];
      const right = librarySort.key === 'updated' ? b.updated_at : b[librarySort.key];
      return String(left || '').localeCompare(String(right || '')) * (librarySort.asc ? 1 : -1);
    });
    return copy;
  }, [library, librarySort]);

  const sentencesBySeason = useMemo(() => {
    const grouped = new Map<SentenceSeason, OpeningSentence[]>();
    for (const season of SEASON_ORDER) grouped.set(season, []);
    for (const sentence of sentences) {
      const bucket = grouped.get(sentence.season);
      if (bucket) bucket.push(sentence);
    }
    return grouped;
  }, [sentences]);

  const toggleScheduleSort = (key: 'date' | 'theme' | 'status') => {
    setScheduleSort(current => ({ key, asc: current.key === key ? !current.asc : true }));
  };

  const toggleLibrarySort = (key: 'kind' | 'citation' | 'status' | 'updated') => {
    setLibrarySort(current => ({ key, asc: current.key === key ? !current.asc : true }));
  };

  const openEditor = (reading?: RehearsalReading) => {
    if (reading) {
      setEditingId(reading.id);
      setReadingForm({
        kind: reading.kind,
        citation: reading.citation,
        author: reading.author || '',
        work: reading.work || '',
        locator: reading.locator || '',
        text_body: reading.text_body || '',
        translation_note: reading.translation_note || '',
        tags: (reading.tags || []).join(', '),
        status: reading.status,
      });
    } else {
      setEditingId(null);
      setReadingForm(blankForm);
    }
    setEditorOpen(true);
  };

  const openSentenceEditor = (sentence?: OpeningSentence) => {
    if (sentence) {
      setEditingSentenceId(sentence.id);
      setSentenceForm({
        text_body: sentence.text_body,
        citation: sentence.citation,
        season: sentence.season,
        source: sentence.source,
        bcp_page: sentence.bcp_page || '',
        drawn_from_reading_id: sentence.drawn_from_reading_id ? String(sentence.drawn_from_reading_id) : '',
        notes: sentence.notes || '',
      });
    } else {
      setEditingSentenceId(null);
      setSentenceForm(blankSentence);
    }
    setSentenceEditorOpen(true);
  };

  const openPrayerEditor = (prayer?: RehearsalPrayer) => {
    if (prayer) {
      setEditingPrayerId(prayer.id);
      setPrayerForm({
        title: prayer.title,
        form: prayer.form,
        rite: prayer.rite,
        body: prayer.body,
        source: prayer.source || '',
        notes: prayer.notes || '',
      });
    } else {
      setEditingPrayerId(null);
      setPrayerForm(blankPrayer);
    }
    setPrayerEditorOpen(true);
  };

  const openPeriodEditor = (period?: PrayerPeriod) => {
    if (period) {
      setEditingPeriodId(period.id);
      setPeriodForm({
        label: period.label,
        prayer_id: String(period.prayer_id),
        starts_on: period.starts_on,
        ends_on: period.ends_on || '',
      });
    } else {
      setEditingPeriodId(null);
      setPeriodForm(blankPeriod);
    }
    setPeriodEditorOpen(true);
  };

  const saveReading = async () => {
    if (!currentChurch?.id || !readingForm.citation.trim()) {
      toast.error('Citation is required.');
      return;
    }
    const wordCount = readingForm.text_body.trim() ? readingForm.text_body.trim().split(/\s+/).length : 0;
    if (readingForm.status === 'ready' && wordCount < 40) {
      toast.error('A Ready reading needs at least a short paragraph (40 words).');
      return;
    }
    const payload = {
      church_id: currentChurch.id,
      ...readingForm,
      citation: readingForm.citation.trim(),
      tags: readingForm.tags.split(',').map(tag => tag.trim()).filter(Boolean),
    };
    try {
      setSaving(true);
      if (editingId) {
        await apiClient.put(`/rehearsal-readings/library/${editingId}?churchId=${currentChurch.id}`, payload);
      } else {
        await apiClient.post('/rehearsal-readings/library', payload);
      }
      setEditorOpen(false);
      toast.success(editingId ? 'Reading updated.' : 'Reading added to the library.');
      await loadData();
    } catch (requestError: any) {
      console.error('Failed to save reading:', requestError);
      toast.error(requestError.response?.data?.error || 'The reading could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const saveSentence = async () => {
    if (!currentChurch?.id) return;
    if (!sentenceForm.text_body.trim() || !sentenceForm.citation.trim()) {
      toast.error('A sentence needs both its text and a citation.');
      return;
    }
    const payload = {
      church_id: currentChurch.id,
      text_body: sentenceForm.text_body.trim(),
      citation: sentenceForm.citation.trim(),
      season: sentenceForm.season,
      source: sentenceForm.source,
      bcp_page: sentenceForm.bcp_page.trim() || null,
      drawn_from_reading_id: sentenceForm.drawn_from_reading_id ? Number(sentenceForm.drawn_from_reading_id) : null,
      notes: sentenceForm.notes.trim() || null,
    };
    try {
      setSaving(true);
      if (editingSentenceId) {
        await apiClient.put(`/rehearsal-readings/opening-sentences/${editingSentenceId}?churchId=${currentChurch.id}`, payload);
      } else {
        await apiClient.post('/rehearsal-readings/opening-sentences', payload);
      }
      setSentenceEditorOpen(false);
      toast.success(editingSentenceId ? 'Opening sentence updated.' : 'Opening sentence added.');
      await loadData();
    } catch (requestError: any) {
      console.error('Failed to save opening sentence:', requestError);
      toast.error(requestError.response?.data?.error || 'The opening sentence could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const savePrayer = async () => {
    if (!currentChurch?.id) return;
    if (!prayerForm.title.trim() || !prayerForm.body.trim()) {
      toast.error('A prayer needs both a title and its text.');
      return;
    }
    const payload = {
      church_id: currentChurch.id,
      title: prayerForm.title.trim(),
      form: prayerForm.form,
      rite: prayerForm.rite,
      body: prayerForm.body,
      source: prayerForm.source.trim() || null,
      notes: prayerForm.notes.trim() || null,
    };
    try {
      setSaving(true);
      if (editingPrayerId) {
        await apiClient.put(`/rehearsal-readings/prayers/${editingPrayerId}?churchId=${currentChurch.id}`, payload);
      } else {
        await apiClient.post('/rehearsal-readings/prayers', payload);
      }
      setPrayerEditorOpen(false);
      toast.success(editingPrayerId ? 'Prayer updated.' : 'Prayer added.');
      await loadData();
    } catch (requestError: any) {
      console.error('Failed to save prayer:', requestError);
      toast.error(requestError.response?.data?.error || 'The prayer could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const savePeriod = async () => {
    if (!currentChurch?.id) return;
    if (!periodForm.label.trim() || !periodForm.prayer_id || !periodForm.starts_on) {
      toast.error('A period needs a name, a prayer, and a start date.');
      return;
    }
    const payload = {
      church_id: currentChurch.id,
      label: periodForm.label.trim(),
      prayer_id: Number(periodForm.prayer_id),
      starts_on: periodForm.starts_on,
      ends_on: periodForm.ends_on || null,
    };
    try {
      setSaving(true);
      if (editingPeriodId) {
        await apiClient.put(`/rehearsal-readings/prayer-periods/${editingPeriodId}?churchId=${currentChurch.id}`, payload);
      } else {
        await apiClient.post('/rehearsal-readings/prayer-periods', payload);
      }
      setPeriodEditorOpen(false);
      toast.success(editingPeriodId ? 'Prayer period updated.' : 'Prayer period added.');
      await loadData();
    } catch (requestError: any) {
      console.error('Failed to save prayer period:', requestError);
      toast.error(requestError.response?.data?.error || 'The prayer period could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const removePeriod = async (period: PrayerPeriod) => {
    if (!currentChurch?.id) return;
    if (!window.confirm(`Remove the ${period.label} prayer period?`)) return;
    try {
      await apiClient.delete(`/rehearsal-readings/prayer-periods/${period.id}?churchId=${currentChurch.id}`);
      toast.success('Prayer period removed.');
      await loadData();
    } catch (requestError: any) {
      console.error('Failed to remove prayer period:', requestError);
      toast.error(requestError.response?.data?.error || 'The prayer period could not be removed.');
    }
  };

  const assignOpeningSentence = async (set: RehearsalReadingSet, sentenceId: string) => {
    if (!currentChurch?.id) return;
    try {
      setAssigningSetId(set.id);
      const response = await apiClient.put(
        `/rehearsal-readings/schedules/${set.id}/opening-sentence?churchId=${currentChurch.id}`,
        { opening_sentence_id: sentenceId ? Number(sentenceId) : null }
      );
      const updated: RehearsalReadingSet = response.data.data;
      setSets(current => current.map(item => (item.id === updated.id ? updated : item)));
    } catch (requestError: any) {
      console.error('Failed to set the opening sentence:', requestError);
      toast.error(requestError.response?.data?.error || 'The opening sentence could not be changed.');
    } finally {
      setAssigningSetId(null);
    }
  };

  const downloadPdf = async (set: RehearsalReadingSet) => {
    if (!currentChurch?.id) return;
    try {
      setDownloadingId(set.id);
      const response = await apiClient.get(
        `/rehearsal-readings/schedules/${set.id}/pdf?churchId=${currentChurch.id}`,
        { responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match?.[1] || `Choir-Rehearsal-Readings-${set.rehearsal_date}.pdf`;
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Reading sheet downloaded.');
    } catch (requestError) {
      console.error('Failed to download reading sheet:', requestError);
      toast.error('The reading sheet could not be exported.');
    } finally {
      setDownloadingId(null);
    }
  };

  const previewReading = (reading: RehearsalReading) => setPreview({
    eyebrow: readingKindLabel(reading.kind),
    title: reading.citation,
    note: reading.translation_note,
    text: reading.text_body || '',
    onEdit: canEdit ? () => openEditor(reading) : undefined,
  });

  const previewSentence = (sentence: OpeningSentence) => setPreview({
    eyebrow: `Opening sentence · ${SEASON_LABELS[sentence.season]}`,
    title: sentence.citation,
    note: [sentenceSourceLabel(sentence), sentence.notes].filter(Boolean).join(' · '),
    text: sentence.text_body,
    onEdit: canEdit ? () => openSentenceEditor(sentence) : undefined,
  });

  const previewPrayer = (prayer: RehearsalPrayer, periodLabel?: string) => setPreview({
    eyebrow: [PRAYER_FORM_LABELS[prayer.form], RITE_LABELS[prayer.rite], periodLabel].filter(Boolean).join(' · '),
    title: prayer.title,
    note: prayer.source,
    text: prayer.body,
    onEdit: canEdit ? () => openPrayerEditor(prayer) : undefined,
  });

  const readingCell = (reading: RehearsalReading) => (
    <div className="reading-cell">
      <button className="reading-link" type="button" onClick={() => previewReading(reading)}>
        {reading.citation}
      </button>
      <div className="mt-1"><StatusBadge status={reading.status} /></div>
    </div>
  );

  if (loading) {
    return (
      <>
        <Navigation />
        <Container className="py-5 text-center">
          <Spinner animation="border" className="mb-3" />
          <div>Loading rehearsal readings…</div>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container fluid="xl" className="py-4 rehearsal-readings-page">
        <div className="d-flex flex-wrap gap-3 align-items-start mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <BookOpen size={28} aria-hidden="true" />
              <h1 className="mb-0">Rehearsal Readings</h1>
            </div>
            <p className="text-muted mb-0">
              Evening Prayer readings and BCP psalmody for choir rehearsal. The opening sentence changes week by week and the prayer changes by the month. The PDF can be downloaded at any stage; paragraphs still in Draft print with their citation only.
            </p>
          </div>
          <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={loadData}>
            <RefreshCw size={15} className="me-1" /> Refresh
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
          <ButtonGroup aria-label="Reading view">
            <Button variant={view === 'schedule' ? 'primary' : 'outline-primary'} onClick={() => setView('schedule')}>
              Rehearsal Schedule
            </Button>
            <Button variant={view === 'library' ? 'primary' : 'outline-primary'} onClick={() => setView('library')}>
              Reading Library
            </Button>
            <Button variant={view === 'sentences' ? 'primary' : 'outline-primary'} onClick={() => setView('sentences')}>
              Opening Sentences
            </Button>
            <Button variant={view === 'prayers' ? 'primary' : 'outline-primary'} onClick={() => setView('prayers')}>
              Prayers
            </Button>
          </ButtonGroup>
          {view === 'library' && canEdit && (
            <Button onClick={() => openEditor()}>
              <Plus size={16} className="me-1" /> New Reading
            </Button>
          )}
          {view === 'sentences' && canEdit && (
            <Button onClick={() => openSentenceEditor()}>
              <Plus size={16} className="me-1" /> New Sentence
            </Button>
          )}
        </div>

        {view === 'schedule' && (
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 rehearsal-table align-middle">
                <thead>
                  <tr>
                    <th><button className="sort-heading" onClick={() => toggleScheduleSort('date')}>Date</button></th>
                    <th><button className="sort-heading" onClick={() => toggleScheduleSort('theme')}>Theme</button></th>
                    <th>Opening sentence</th>
                    <th>BCP psalmody</th>
                    <th>Scripture</th>
                    <th>Christian lineage</th>
                    <th>Prayers</th>
                    <th><button className="sort-heading" onClick={() => toggleScheduleSort('status')}>Status</button></th>
                    {canEdit && <th>Edit</th>}
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSets.map(set => (
                    <tr key={set.id}>
                      <td className="text-nowrap fw-semibold">{formatDate(set.rehearsal_date)}</td>
                      <td>{set.theme}</td>
                      <td className="sentence-cell">
                        {set.opening_sentence ? (
                          <button className="reading-link" type="button" onClick={() => previewSentence(set.opening_sentence!)}>
                            {set.opening_sentence.citation}
                          </button>
                        ) : (
                          <span className="text-muted">None chosen</span>
                        )}
                        {set.opening_sentence && (
                          <small className="text-muted d-block">{truncate(set.opening_sentence.text_body, 64)}</small>
                        )}
                        {canEdit && (
                          <Form.Select
                            size="sm"
                            className="mt-1"
                            disabled={assigningSetId === set.id}
                            value={set.opening_sentence_id ? String(set.opening_sentence_id) : ''}
                            onChange={event => assignOpeningSentence(set, event.target.value)}
                          >
                            <option value="">No opening sentence</option>
                            {SEASON_ORDER.map(season => {
                              const group = sentencesBySeason.get(season) || [];
                              if (group.length === 0) return null;
                              return (
                                <optgroup key={season} label={SEASON_LABELS[season]}>
                                  {group.map(sentence => (
                                    <option key={sentence.id} value={sentence.id}>
                                      {sentence.citation} — {truncate(sentence.text_body, 44)}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </Form.Select>
                        )}
                      </td>
                      <td className="psalmody-cell">
                        <div className="fw-semibold">
                          {set.canticle_name} <span className="text-muted fw-normal">(BCP {set.canticle_bcp_page})</span>
                        </div>
                        <small className="text-muted d-block mb-1">
                          {set.canticle_position === 'before_psalms' ? 'before psalms' : 'after Scripture'}
                        </small>
                        {set.psalmody.map(psalm => (
                          <div key={psalm.citation}>
                            {psalm.citation} <span className="text-muted">· BCP {psalm.bcpPages}</span>
                          </div>
                        ))}
                      </td>
                      <td>{readingCell(set.scripture)}</td>
                      <td>{readingCell(set.christian_lineage)}</td>
                      <td className="prayer-cell">
                        {set.prayer ? (
                          <>
                            <button className="reading-link" type="button" onClick={() => previewPrayer(set.prayer!, set.prayer!.period_label)}>
                              {set.prayer.title}
                            </button>
                            <small className="text-muted d-block">{set.prayer.period_label}</small>
                          </>
                        ) : (
                          <span className="text-muted">No period covers this date</span>
                        )}
                      </td>
                      <td><StatusBadge status={set.status} /></td>
                      {canEdit && (
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Button variant="outline-secondary" size="sm" onClick={() => openEditor(set.scripture)}>
                              <Pencil size={13} className="me-1" /> Scripture
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => openEditor(set.christian_lineage)}>
                              <Pencil size={13} className="me-1" /> Lineage
                            </Button>
                          </div>
                        </td>
                      )}
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={downloadingId === set.id}
                          onClick={() => downloadPdf(set)}
                          title="Download the reading sheet"
                        >
                          {downloadingId === set.id ? <Spinner size="sm" animation="border" /> : <FileDown size={16} />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {sortedSets.length === 0 && <div className="p-4 text-center text-muted">No rehearsal dates have been added.</div>}
            </Card.Body>
          </Card>
        )}

        {view === 'library' && (
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 rehearsal-table align-middle">
                <thead>
                  <tr>
                    <th><button className="sort-heading" onClick={() => toggleLibrarySort('kind')}>Type</button></th>
                    <th><button className="sort-heading" onClick={() => toggleLibrarySort('citation')}>Citation</button></th>
                    <th>Source</th>
                    <th>Tags</th>
                    <th>Scheduled</th>
                    <th><button className="sort-heading" onClick={() => toggleLibrarySort('status')}>Status</button></th>
                    {canEdit && <th>Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedLibrary.map(reading => (
                    <tr key={reading.id}>
                      <td className="text-nowrap">{readingKindLabel(reading.kind)}</td>
                      <td>
                        <button className="reading-link" type="button" onClick={() => previewReading(reading)}>
                          {reading.citation}
                        </button>
                      </td>
                      <td>
                        {reading.author && <div>{reading.author}</div>}
                        {reading.work && <small className="text-muted">{reading.work}{reading.locator ? `, ${reading.locator}` : ''}</small>}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(reading.tags || []).map(tag => <Badge bg="light" text="dark" key={tag}>{tag}</Badge>)}
                        </div>
                      </td>
                      <td className="small">
                        {(reading.usage_dates || []).length
                          ? reading.usage_dates!.sort().map(date => formatDate(date, true)).join(', ')
                          : <span className="text-muted">Not scheduled</span>}
                      </td>
                      <td><StatusBadge status={reading.status} /></td>
                      {canEdit && (
                        <td>
                          <Button variant="outline-secondary" size="sm" onClick={() => openEditor(reading)}>
                            <Pencil size={14} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {view === 'sentences' && (
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 rehearsal-table align-middle">
                <thead>
                  <tr>
                    <th>Season</th>
                    <th>Citation</th>
                    <th>Sentence</th>
                    <th>Source</th>
                    <th>Drawn from</th>
                    <th>Scheduled</th>
                    {canEdit && <th>Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {sentences.map(sentence => (
                    <tr key={sentence.id}>
                      <td className="text-nowrap">{SEASON_LABELS[sentence.season]}</td>
                      <td className="text-nowrap">
                        <button className="reading-link" type="button" onClick={() => previewSentence(sentence)}>
                          {sentence.citation}
                        </button>
                      </td>
                      <td>{truncate(sentence.text_body, 120)}</td>
                      <td className="text-nowrap">
                        <Badge bg={sentence.source === 'bcp' ? 'light' : 'info'} text={sentence.source === 'bcp' ? 'dark' : undefined}>
                          {sentenceSourceLabel(sentence)}
                        </Badge>
                      </td>
                      <td className="small">
                        {sentence.drawn_from_citation || <span className="text-muted">—</span>}
                      </td>
                      <td className="small">
                        {(sentence.usage_dates || []).length
                          ? sentence.usage_dates!.sort().map(date => formatDate(date, true)).join(', ')
                          : <span className="text-muted">Not scheduled</span>}
                      </td>
                      {canEdit && (
                        <td>
                          <Button variant="outline-secondary" size="sm" onClick={() => openSentenceEditor(sentence)}>
                            <Pencil size={14} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              {sentences.length === 0 && <div className="p-4 text-center text-muted">No opening sentences yet.</div>}
            </Card.Body>
          </Card>
        )}

        {view === 'prayers' && (
          <>
            <Card className="shadow-sm mb-4">
              <Card.Header className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div>
                  <strong>Which prayer is in use</strong>
                  <div className="text-muted small">
                    A period covers a stretch of rehearsals, so the prayer changes about once a month rather than every week.
                  </div>
                </div>
                {canEdit && (
                  <Button size="sm" onClick={() => openPeriodEditor()}>
                    <Plus size={16} className="me-1" /> New Period
                  </Button>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0 rehearsal-table align-middle">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Dates</th>
                      <th>Prayer</th>
                      <th>Source</th>
                      {canEdit && <th>Edit</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map(period => (
                      <tr key={period.id}>
                        <td className="fw-semibold text-nowrap">{period.label}</td>
                        <td className="small text-nowrap">
                          {formatDate(period.starts_on, true)} – {period.ends_on ? formatDate(period.ends_on, true) : 'onward'}
                        </td>
                        <td>
                          <button
                            className="reading-link"
                            type="button"
                            onClick={() => {
                              const prayer = prayers.find(item => item.id === period.prayer_id);
                              if (prayer) previewPrayer(prayer, period.label);
                            }}
                          >
                            {period.prayer_title}
                          </button>
                          {period.prayer_form && (
                            <small className="text-muted d-block">
                              {PRAYER_FORM_LABELS[period.prayer_form]}
                              {period.prayer_rite ? ` · ${RITE_LABELS[period.prayer_rite]}` : ''}
                            </small>
                          )}
                        </td>
                        <td className="small text-muted">{period.prayer_source || '—'}</td>
                        {canEdit && (
                          <td>
                            <div className="d-flex gap-1">
                              <Button variant="outline-secondary" size="sm" onClick={() => openPeriodEditor(period)}>
                                <Pencil size={14} />
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={() => removePeriod(period)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {periods.length === 0 && <div className="p-4 text-center text-muted">No periods have been set, so no prayer is printing yet.</div>}
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Header className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <strong>Prayer library</strong>
                {canEdit && (
                  <Button size="sm" onClick={() => openPrayerEditor()}>
                    <Plus size={16} className="me-1" /> New Prayer
                  </Button>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0 rehearsal-table align-middle">
                  <thead>
                    <tr>
                      <th>Form</th>
                      <th>Title</th>
                      <th>Rite</th>
                      <th>Source</th>
                      <th>Used in</th>
                      {canEdit && <th>Edit</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {prayers.map(prayer => (
                      <tr key={prayer.id}>
                        <td className="text-nowrap">{PRAYER_FORM_LABELS[prayer.form]}</td>
                        <td>
                          <button className="reading-link" type="button" onClick={() => previewPrayer(prayer)}>
                            {prayer.title}
                          </button>
                          <small className="text-muted d-block">{truncate(prayer.body, 90)}</small>
                        </td>
                        <td className="text-nowrap">{RITE_LABELS[prayer.rite]}</td>
                        <td className="small text-muted">{prayer.source || '—'}</td>
                        <td className="small">
                          {(prayer.period_labels || []).length
                            ? prayer.period_labels!.join(', ')
                            : <span className="text-muted">Not scheduled</span>}
                        </td>
                        {canEdit && (
                          <td>
                            <Button variant="outline-secondary" size="sm" onClick={() => openPrayerEditor(prayer)}>
                              <Pencil size={14} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {prayers.length === 0 && <div className="p-4 text-center text-muted">No prayers in the library yet.</div>}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>

      <Modal show={preview !== null} onHide={() => setPreview(null)} size="lg" centered>
        {preview && (
          <>
            <Modal.Header closeButton>
              <div>
                <div className="text-uppercase small text-muted fw-semibold">{preview.eyebrow}</div>
                <Modal.Title>{preview.title}</Modal.Title>
              </div>
            </Modal.Header>
            <Modal.Body>
              {preview.note && <p className="fst-italic text-muted">{preview.note}</p>}
              {preview.text
                ? <div className="reading-preview-text">{preview.text}</div>
                : <Alert variant="secondary" className="mb-0">This paragraph has not been prepared yet.</Alert>}
            </Modal.Body>
            <Modal.Footer>
              {preview.onEdit && (
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    const edit = preview.onEdit!;
                    setPreview(null);
                    edit();
                  }}
                >
                  Edit
                </Button>
              )}
              <Button variant="secondary" onClick={() => setPreview(null)}>Close</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={editorOpen} onHide={() => !saving && setEditorOpen(false)} size="lg" backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingId ? 'Edit Reading' : 'New Reading'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-4">
                <Form.Label>Type</Form.Label>
                <Form.Select value={readingForm.kind} onChange={event => setReadingForm({ ...readingForm, kind: event.target.value as ReadingKind })}>
                  <option value="scripture">Scripture</option>
                  <option value="christian_lineage">Christian lineage</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-8">
                <Form.Label>Citation</Form.Label>
                <Form.Control required value={readingForm.citation} onChange={event => setReadingForm({ ...readingForm, citation: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Author</Form.Label>
                <Form.Control value={readingForm.author} onChange={event => setReadingForm({ ...readingForm, author: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-5">
                <Form.Label>Work</Form.Label>
                <Form.Control value={readingForm.work} onChange={event => setReadingForm({ ...readingForm, work: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Locator</Form.Label>
                <Form.Control value={readingForm.locator} onChange={event => setReadingForm({ ...readingForm, locator: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Reading text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={9}
                  value={readingForm.text_body}
                  onChange={event => setReadingForm({ ...readingForm, text_body: event.target.value })}
                  placeholder="Enter at least a paragraph for oral reading."
                />
              </Form.Group>
              <Form.Group className="col-md-8">
                <Form.Label>Text or translation note</Form.Label>
                <Form.Control value={readingForm.translation_note} onChange={event => setReadingForm({ ...readingForm, translation_note: event.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Status</Form.Label>
                <Form.Select value={readingForm.status} onChange={event => setReadingForm({ ...readingForm, status: event.target.value as ReadingStatus })}>
                  <option value="draft">Draft</option>
                  <option value="ready">Ready</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Tags</Form.Label>
                <Form.Control value={readingForm.tags} onChange={event => setReadingForm({ ...readingForm, tags: event.target.value })} placeholder="listening, prayer, psalmody" />
                <Form.Text muted>Separate tags with commas.</Form.Text>
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button disabled={saving} onClick={saveReading}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save Reading
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={sentenceEditorOpen} onHide={() => !saving && setSentenceEditorOpen(false)} size="lg" backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingSentenceId ? 'Edit Opening Sentence' : 'New Opening Sentence'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-12">
                <Form.Label>The sentence</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={sentenceForm.text_body}
                  onChange={event => setSentenceForm({ ...sentenceForm, text_body: event.target.value })}
                  placeholder="A verse or two, the length the prayer book uses."
                />
              </Form.Group>
              <Form.Group className="col-md-5">
                <Form.Label>Citation</Form.Label>
                <Form.Control
                  value={sentenceForm.citation}
                  onChange={event => setSentenceForm({ ...sentenceForm, citation: event.target.value })}
                  placeholder="Psalm 122:1"
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Season</Form.Label>
                <Form.Select value={sentenceForm.season} onChange={event => setSentenceForm({ ...sentenceForm, season: event.target.value as SentenceSeason })}>
                  {SEASON_ORDER.map(season => (
                    <option key={season} value={season}>{SEASON_LABELS[season]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Where it comes from</Form.Label>
                <Form.Select value={sentenceForm.source} onChange={event => setSentenceForm({ ...sentenceForm, source: event.target.value as SentenceSource })}>
                  <option value="bcp">Prayer book</option>
                  <option value="parish">Ours</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>BCP page</Form.Label>
                <Form.Control
                  value={sentenceForm.bcp_page}
                  onChange={event => setSentenceForm({ ...sentenceForm, bcp_page: event.target.value })}
                  placeholder="78"
                />
                <Form.Text muted>Leave empty for a sentence we chose ourselves.</Form.Text>
              </Form.Group>
              <Form.Group className="col-md-8">
                <Form.Label>Drawn from a reading</Form.Label>
                <Form.Select
                  value={sentenceForm.drawn_from_reading_id}
                  onChange={event => setSentenceForm({ ...sentenceForm, drawn_from_reading_id: event.target.value })}
                >
                  <option value="">Not tied to a reading</option>
                  {library.map(reading => (
                    <option key={reading.id} value={reading.id}>
                      {readingKindLabel(reading.kind)} — {reading.citation}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>For a sentence we pulled out of that evening&rsquo;s reading.</Form.Text>
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Note</Form.Label>
                <Form.Control value={sentenceForm.notes} onChange={event => setSentenceForm({ ...sentenceForm, notes: event.target.value })} />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setSentenceEditorOpen(false)}>Cancel</Button>
          <Button disabled={saving} onClick={saveSentence}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save Sentence
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={prayerEditorOpen} onHide={() => !saving && setPrayerEditorOpen(false)} size="lg" backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingPrayerId ? 'Edit Prayer' : 'New Prayer'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-6">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={prayerForm.title}
                  onChange={event => setPrayerForm({ ...prayerForm, title: event.target.value })}
                  placeholder="A Collect for Peace"
                />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Form</Form.Label>
                <Form.Select value={prayerForm.form} onChange={event => setPrayerForm({ ...prayerForm, form: event.target.value as PrayerForm })}>
                  <option value="suffrages">Suffrages</option>
                  <option value="collect">Collect</option>
                  <option value="prayer">Prayer</option>
                  <option value="thanksgiving">Thanksgiving</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Rite</Form.Label>
                <Form.Select value={prayerForm.rite} onChange={event => setPrayerForm({ ...prayerForm, rite: event.target.value as PrayerRite })}>
                  <option value="one">Rite One</option>
                  <option value="two">Rite Two</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={prayerForm.body}
                  onChange={event => setPrayerForm({ ...prayerForm, body: event.target.value })}
                  placeholder={'V. O Lord, save thy people and bless thine heritage;\nR. Govern them and lift them up for ever.'}
                />
                <Form.Text muted>
                  Line breaks are kept as written. A line beginning with &ldquo;R.&rdquo; prints in italic as the response.
                </Form.Text>
              </Form.Group>
              <Form.Group className="col-md-8">
                <Form.Label>Source</Form.Label>
                <Form.Control
                  value={prayerForm.source}
                  onChange={event => setPrayerForm({ ...prayerForm, source: event.target.value })}
                  placeholder="BCP p. 55, Daily Morning Prayer: Rite One"
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Note</Form.Label>
                <Form.Control value={prayerForm.notes} onChange={event => setPrayerForm({ ...prayerForm, notes: event.target.value })} />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setPrayerEditorOpen(false)}>Cancel</Button>
          <Button disabled={saving} onClick={savePrayer}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save Prayer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={periodEditorOpen} onHide={() => !saving && setPeriodEditorOpen(false)} backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingPeriodId ? 'Edit Prayer Period' : 'New Prayer Period'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-12">
                <Form.Label>Period</Form.Label>
                <Form.Control
                  value={periodForm.label}
                  onChange={event => setPeriodForm({ ...periodForm, label: event.target.value })}
                  placeholder="October 2026"
                />
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label>Prayer</Form.Label>
                <Form.Select value={periodForm.prayer_id} onChange={event => setPeriodForm({ ...periodForm, prayer_id: event.target.value })}>
                  <option value="">Choose a prayer</option>
                  {prayers.map(prayer => (
                    <option key={prayer.id} value={prayer.id}>
                      {prayer.title} ({PRAYER_FORM_LABELS[prayer.form]}, {RITE_LABELS[prayer.rite]})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label>Starts</Form.Label>
                <Form.Control
                  type="date"
                  value={periodForm.starts_on}
                  onChange={event => setPeriodForm({ ...periodForm, starts_on: event.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label>Ends</Form.Label>
                <Form.Control
                  type="date"
                  value={periodForm.ends_on}
                  onChange={event => setPeriodForm({ ...periodForm, ends_on: event.target.value })}
                />
                <Form.Text muted>Leave empty to run until the next period begins.</Form.Text>
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setPeriodEditorOpen(false)}>Cancel</Button>
          <Button disabled={saving} onClick={savePeriod}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save Period
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RehearsalReadingsPage;
